import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast.service';
import { ApiService } from '../services/api.service';
import { LoadingService } from '../services/loading.service';
import { takeUntil } from 'rxjs/operators';

export interface MenuPermissions {
  [menuCategory: string]: string[];
}

interface JwtPayload {
  exp?: number;
  roles?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ServicioAutenticacion {
  constructor(
    private router: Router,
    private toastService: ToastService,
    private apiService: ApiService,
    private loadingService: LoadingService
  ) {}

  public static readonly MENU_PERMISSIONS: MenuPermissions = {
    inicio: ['ADMIN', 'GESTOR-PERSONAS', 'GESTOR-ARCHIVOS'],
    personas: ['ADMIN', 'GESTOR-PERSONAS', 'GESTOR-ARCHIVOS'],
    planes: ['ADMIN'],
    usuarios: ['ADMIN'],
    archivos: ['ADMIN', 'GESTOR-ARCHIVOS'],
    reportes: ['ADMIN', 'GESTOR-PERSONAS', 'GESTOR-ARCHIVOS'],
    gestos: ['ADMIN', 'GESTOR-ARCHIVOS'],
  };

  private readonly TOKEN_KEY = 'token';
  private autenticado = new BehaviorSubject<boolean>(
    this.verificarSesionIniciada()
  );

  private logoutCountdown: any;
  private countdownInterval: any;
  private readonly LOGOUT_DELAY = 10; // seconds

  estaAutenticado(): Observable<boolean> {
    return this.autenticado.asObservable();
  }

  iniciarSesion(token: string): void {
    localStorage.setItem('sesionIniciada', 'true');
    localStorage.setItem(this.TOKEN_KEY, token);
    this.autenticado.next(true);
  }

  cerrarSesion(): void {
    this.limpiarDatosSesion();
    this.router.navigate(['/login']);
  }

  private verificarSesionIniciada(): boolean {
    const token = localStorage.getItem(this.TOKEN_KEY);
    return !!token && !this.tokenExpirado(token);
  }

  private tokenExpirado(token: string): boolean {
    if (!token || token.split('.').length !== 3) {
      return true;
    }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000;
      return Date.now() > exp;
    } catch (error) {
      return true;
    }
  }

  obtenerToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  obtenerTiempoRestanteSesionFormateado(): string {
    const token = this.obtenerToken();

    if (!token) {
      return '0 segundos';
    }

    try {
      const decoded = jwtDecode<JwtPayload>(token);

      if (!decoded.exp) {
        return '0 segundos';
      }

      const segundosRestantes = Math.max(
        0,
        Math.ceil((decoded.exp * 1000 - Date.now()) / 1000),
      );

      return this.formatearDuracionSesion(segundosRestantes);
    } catch (error) {
      return '0 segundos';
    }
  }

  private formatearDuracionSesion(segundosTotales: number): string {
    const minutos = Math.floor(segundosTotales / 60);
    const segundos = segundosTotales % 60;
    const partes: string[] = [];

    if (minutos > 0) {
      partes.push(`${minutos} ${minutos === 1 ? 'minuto' : 'minutos'}`);
    }

    if (segundos > 0 || partes.length === 0) {
      partes.push(`${segundos} ${segundos === 1 ? 'segundo' : 'segundos'}`);
    }

    return partes.join(' y ');
  }

  obtenerRolesDesdeToken(): string[] {
    const token = this.obtenerToken();
    if (token) {
      const decoded = jwtDecode<JwtPayload>(token);
      const roles: string[] = decoded.roles || [];
      return roles.map((role) => role.replace(/^ROLE_/, ''));
    }
    return [];
  }

  static getMenuPermissions(): MenuPermissions {
    return ServicioAutenticacion.MENU_PERMISSIONS;
  }

  logout(mensaje?: string): void {
    if (mensaje) {
      this.toastService.mostrarNotificacion(mensaje, 'info');
    }
    this.cerrarSesion();
  }

  handleSessionExpired(): void {
    this.clearExistingCountdowns();
    this.loadingService.setLoading(true);
    this.toastService.mostrarNotificacion(
      `Su sesión ha expirado. Se cerrará automáticamente en ${this.LOGOUT_DELAY} segundos. Vuelva a iniciar sesión.`,
      'warning'
    );

    this.logoutCountdown = setTimeout(() => {
      this.limpiarDatosSesion();
      this.loadingService.setLoading(false);
      this.router.navigate(['/login']);
    }, this.LOGOUT_DELAY * 1000);

    let remainingSeconds = this.LOGOUT_DELAY - 1;
    this.countdownInterval = setInterval(() => {
      if (remainingSeconds > 0) {
        this.toastService.mostrarNotificacion(
          `Su sesión ha expirado. Se cerrará automáticamente en ${remainingSeconds} segundos. Vuelva a iniciar sesión.`,
          'warning'
        );
        remainingSeconds--;
      } else {
        clearInterval(this.countdownInterval);
      }
    }, 1000);
  }

  private clearExistingCountdowns(): void {
    if (this.logoutCountdown) {
      clearTimeout(this.logoutCountdown);
      this.logoutCountdown = null;
    }

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private limpiarDatosSesion(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem('sesionIniciada');
    localStorage.removeItem('submenuState');
    localStorage.removeItem('usuario');
    this.autenticado.next(false);
  }

  handleForbiddenError(): void {
    this.toastService.mostrarNotificacion(
      'No tiene permisos para acceder a este recurso. En caso que sea un error, inicie sesión nuevamente ',
      'error'
    );
    this.router.navigate(['/home']);
  }
}
